/* =========================================================================
   LandingPage7 — Grand Slam Offer landing page for the "From Idea to App"
   course: build your own software with AI, in Spanish, no engineer / no
   English. Structure follows Alex Hormozi's $100M Offers: hero (MAGIC
   headline) → the problem → how it works → cost comparison + offer → guarantee →
   final CTA. Copy is English for now (lp6.* keys); the Spanish view falls back
   to it until translated. Everything is scoped under .lp7 so it never collides
   with the Argon globals or the shelved LandingPage5.
   ========================================================================= */

import React, { useEffect } from "react";

// reactstrap components
import { Container, Row, Col } from "reactstrap";

import "./LandingPage7.css";

// icons
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faLaptopCode,
  faDollarSign,
  faLanguage,
  faUsers,
  faLightbulb,
  faRobot,
  faHeadphones,
  faRocket,
  faCheck,
  faShieldHalved,
  faClock,
  faGraduationCap,
  faUserTie,
  faInfinity,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";

// core components
import DemoNavbar from "components/Navbars/DemoNavbar.js";
import MainFooter from "components/Footers/MainFooter.js";
import ValueStack, { SHOW_STACK_PRICES } from "components/Offer/ValueStack.js";
import { ENROLLMENT_OPEN } from "../../config/enrollment";

import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

// The fears that keep the idea stuck (Hormozi step 2: list the problems).
const PROBLEMS = [
  { n: 1, icon: faLaptopCode, tone: "lp7-ic-cyan" },
  { n: 2, icon: faDollarSign, tone: "lp7-ic-purple" },
  { n: 3, icon: faLanguage, tone: "lp7-ic-blue" },
  { n: 4, icon: faUsers, tone: "lp7-ic-cyan" },
];

// How it works — the believable path to the dream outcome.
const STEPS = [
  { n: 1, icon: faLightbulb, tone: "lp7-ic-cyan" },
  { n: 2, icon: faRobot, tone: "lp7-ic-purple" },
  { n: 3, icon: faHeadphones, tone: "lp7-ic-blue" },
  { n: 4, icon: faRocket, tone: "lp7-ic-cyan" },
];

const LandingPage7 = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Transparent navbar over the hero, fading to solid navy on scroll. Body
  // classes are cleaned up on unmount so every other page keeps the solid one.
  useEffect(() => {
    document.body.classList.add("lp7-active");
    const onScroll = () => {
      document.body.classList.toggle("lp7-nav-solid", window.scrollY > 40);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      document.body.classList.remove("lp7-active", "lp7-nav-solid");
      window.removeEventListener("scroll", onScroll);
    };
  }, []);

  const goEnroll = () => navigate("/subscriptions");
  const scrollToOffer = () => {
    const el = document.getElementById("lp7-offer");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <>
      <DemoNavbar />
      <main className="lp7">
        {/* ----------------------------- HERO ----------------------------- */}
        <section className="lp7-hero">
          <span className="lp7-orb lp7-orb-1" />
          <span className="lp7-orb lp7-orb-2" />
          <span className="lp7-orb lp7-orb-3" />

          <Container>
            <Row>
              <Col lg="7">
                <div className="lp7-glass">
                  <span className="lp7-eyebrow">
                    <span className="dot" />
                    {t("lp6.eyebrow")}
                  </span>
                  <h1 className="lp7-title">{t("lp6.heroTitle")}</h1>
                  <p className="lp7-lead">{t("lp6.heroLead")}</p>

                  <div className="lp7-actions">
                    <button className="lp7-btn-primary" onClick={goEnroll}>
                      {t("lp6.heroPrimary")}
                    </button>
                    <button className="lp7-btn-ghost" onClick={scrollToOffer}>
                      {t("lp6.heroSecondary")}
                    </button>
                  </div>

                  <div className="lp7-pills">
                    <span className="lp7-pill">{t("lp6.pill1")}</span>
                    <span className="lp7-pill">{t("lp6.pill2")}</span>
                    <span className="lp7-pill">{t("lp6.pill3")}</span>
                  </div>
                </div>
              </Col>

              <Col lg="5">
                <div className="lp7-preview-wrap">
                  <div className="lp7-preview">
                    <div className="lp7-dots">
                      <span />
                      <span />
                      <span />
                    </div>
                    <div className="lp7-code">
                      <div className="lp7-code-line">
                        <div className="lp7-bar cyan" style={{ width: "34%" }} />
                        <div className="lp7-bar mute" style={{ width: "24%" }} />
                      </div>
                      <div className="lp7-code-line">
                        <div className="lp7-bar purple" style={{ width: "20%" }} />
                        <div className="lp7-bar mute" style={{ width: "44%" }} />
                      </div>
                      <div className="lp7-code-line">
                        <div className="lp7-bar dim" style={{ width: "56%" }} />
                      </div>
                    </div>
                    <div className="lp7-bubble">
                      <div className="lp7-avatar" />
                      <div className="lp7-bubble-lines">
                        <div className="lp7-bar" style={{ width: "90%" }} />
                        <div className="lp7-bar" style={{ width: "68%" }} />
                      </div>
                    </div>
                    <span className="lp7-ask">{t("lp6.previewAsk")}</span>
                  </div>
                </div>
              </Col>
            </Row>
          </Container>
        </section>

        {/* --------------------------- PROBLEM ---------------------------- */}
        <section className="lp7-problem">
          <Container>
            <div className="lp7-section-head lp7-head-dark">
              <h2>{t("lp6.problemTitle")}</h2>
              <p>{t("lp6.problemSubtitle")}</p>
            </div>
            <Row>
              {PROBLEMS.map((p) => (
                <Col lg="3" md="6" className="lp7-col" key={p.n}>
                  <div className="lp7-card lp7-card-dark">
                    <div className={`lp7-card-icon ${p.tone}`}>
                      <FontAwesomeIcon icon={p.icon} />
                    </div>
                    <h6>{t(`lp6.problem${p.n}T`)}</h6>
                    <p>{t(`lp6.problem${p.n}P`)}</p>
                  </div>
                </Col>
              ))}
            </Row>
          </Container>
        </section>

        {/* ------------------------- HOW IT WORKS ------------------------- */}
        <section className="lp7-how">
          <Container>
            <div className="lp7-section-head">
              <h2>{t("lp6.howTitle")}</h2>
              <p>{t("lp6.howSubtitle")}</p>
            </div>
            <Row>
              {STEPS.map((s, i) => (
                <Col lg="3" md="6" className="lp7-col" key={s.n}>
                  <div className="lp7-step">
                    <span className="lp7-step-num">{i + 1}</span>
                    <div className={`lp7-card-icon ${s.tone}`}>
                      <FontAwesomeIcon icon={s.icon} />
                    </div>
                    <h6>{t(`lp6.how${s.n}T`)}</h6>
                    <p>{t(`lp6.how${s.n}P`)}</p>
                  </div>
                </Col>
              ))}
            </Row>
          </Container>
        </section>

        {/* --------------------------- THE ANCHOR ------------------------- */}
        {/* Hormozi price anchor: frame the cost of the real alternatives
            (college / hiring an engineer) right before the offer, so the
            $497 lands soft. */}
        <section className="lp7-anchor">
          <Container>
            <div className="lp7-section-head lp7-head-dark">
              <span className="lp7-eyebrow-2 lp7-eyebrow-light">
                {t("lp6.anchorEyebrow")}
              </span>
              <h2>{t("lp6.anchorTitle")}</h2>
              <p>{t("lp6.anchorSubtitle")}</p>
            </div>

            <Row className="lp7-anchor-grid">
              <Col lg="4" md="6" className="lp7-col">
                <div className="lp7-anchor-card">
                  <span className="lp7-anchor-mark cross">
                    <FontAwesomeIcon icon={faXmark} />
                  </span>
                  <div className="lp7-anchor-ic">
                    <FontAwesomeIcon icon={faGraduationCap} />
                  </div>
                  <h6>{t("lp6.anchorCollegeLabel")}</h6>
                  <div className="lp7-anchor-cost">
                    {t("lp6.anchorCollegeCost")}
                  </div>
                  <p>{t("lp6.anchorCollegeNote")}</p>
                </div>
              </Col>

              <Col lg="4" md="6" className="lp7-col">
                <div className="lp7-anchor-card">
                  <span className="lp7-anchor-mark cross">
                    <FontAwesomeIcon icon={faXmark} />
                  </span>
                  <div className="lp7-anchor-ic">
                    <FontAwesomeIcon icon={faUserTie} />
                  </div>
                  <h6>{t("lp6.anchorEngineerLabel")}</h6>
                  <div className="lp7-anchor-cost">
                    {t("lp6.anchorEngineerCost")}
                  </div>
                  <p>{t("lp6.anchorEngineerNote")}</p>
                </div>
              </Col>

              <Col lg="4" md="6" className="lp7-col">
                <div className="lp7-anchor-card win">
                  <span className="lp7-anchor-mark check">
                    <FontAwesomeIcon icon={faCheck} />
                  </span>
                  <div className="lp7-anchor-ic">
                    <FontAwesomeIcon icon={faInfinity} />
                  </div>
                  <h6>{t("lp6.anchorYouLabel")}</h6>
                  <div className="lp7-anchor-cost">
                    {t("lp6.anchorYouCost")}
                  </div>
                  <p>{t("lp6.anchorYouNote")}</p>
                </div>
              </Col>
            </Row>

            <p className="lp7-anchor-punch">{t("lp6.anchorPunch")}</p>
          </Container>
        </section>

        {/* --------------------- OFFER / WHAT'S INCLUDED ------------------- */}
        <section className="lp7-offer" id="lp7-offer">
          <Container>
            <div className="lp7-section-head">
              <span className="lp7-eyebrow-2">{t("lp6.stackEyebrow")}</span>
              <h2>{t("lp6.stackTitle")}</h2>
              <p>{t("lp6.stackSubtitle")}</p>
            </div>

            <Row className="lp7-offer-grid">
              <ValueStack prefix="lp7" />

              <Col lg="5">
                <div className="lp7-price-card">
                  <span className="lp7-price-eyebrow">
                    {t("lp6.priceEyebrow")}
                  </span>
                  <h3 className="lp7-price-title">{t("lp6.priceTitle")}</h3>
                  <p className="lp7-price-sub">{t("lp6.priceSubtitle")}</p>

                  <div className="lp7-price-amount">
                    {/* The struck-through price is the value-stack total, so it
                        only makes sense while the stack is on the page. With the
                        stack shelved, the comparison line below carries the
                        anchor instead — using a real cost, not a self-assigned
                        one. */}
                    {SHOW_STACK_PRICES && (
                      <span className="lp7-price-strike">
                        {t("lp6.stackTotalValue")}
                      </span>
                    )}
                    <span className="lp7-price-now">{t("lp6.priceAmount")}</span>
                  </div>
                  <span className="lp7-price-plan">{t("lp6.pricePlan")}</span>
                  {!SHOW_STACK_PRICES && (
                    <span className="lp7-price-compare">
                      {t("lp6.priceCompare")}
                    </span>
                  )}

                  <button
                    className="lp7-btn-primary lp7-price-btn"
                    onClick={goEnroll}
                  >
                    {/* Don't promise a seat we can't sell yet — while enrollment
                        is closed this reads "Coming soon" and the offer page it
                        opens explains why. */}
                    {ENROLLMENT_OPEN
                      ? t("lp6.priceCta")
                      : t("enrollment.comingSoonCta")}
                  </button>

                  <div className="lp7-price-meta">
                    <span>
                      <FontAwesomeIcon icon={faUsers} /> {t("lp6.priceSeats")}
                    </span>
                    <span>
                      <FontAwesomeIcon icon={faClock} /> {t("lp6.priceStart")}
                    </span>
                  </div>

                  <div className="lp7-price-guarantee">
                    <FontAwesomeIcon icon={faShieldHalved} />
                    <span>{t("lp6.guaranteeTitle")}</span>
                  </div>
                </div>
              </Col>
            </Row>
          </Container>
        </section>

        {/* --------------------------- GUARANTEE -------------------------- */}
        <section className="lp7-guarantee">
          <Container>
            <div className="lp7-guarantee-card">
              <div className="lp7-guarantee-badge">
                <FontAwesomeIcon icon={faShieldHalved} />
              </div>
              <div>
                <span className="lp7-eyebrow-2">
                  {t("lp6.guaranteeEyebrow")}
                </span>
                <h3>{t("lp6.guaranteeTitle")}</h3>
                <p>{t("lp6.guaranteeBody")}</p>
              </div>
            </div>
          </Container>
        </section>

        {/* --------------------------- FINAL CTA -------------------------- */}
        <section className="lp7-cta">
          <Container>
            <div className="lp7-cta-card">
              <Row>
                <Col lg="8">
                  <h3>{t("lp6.finalTitle")}</h3>
                  <p>{t("lp6.finalBody")}</p>
                </Col>
                <Col lg="4" className="ml-lg-auto">
                  <button className="lp7-cta-btn" onClick={goEnroll}>
                    {t("lp6.finalCta")}
                  </button>
                </Col>
              </Row>
            </div>
          </Container>
        </section>
      </main>
      <MainFooter />
    </>
  );
};

export default LandingPage7;
